import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { footerLinks, contactInfo } from '../mockData';
import { Instagram } from 'lucide-react';
import { useChristmasTheme } from '../hooks/useChristmasTheme';
import { useRepublicDayTheme } from '../hooks/useRepublicDayTheme';
import investorCharterPdf from '../assets/pdf/INVESTOR CHARTER.pdf';

const Footer = () => {
  const location = useLocation();
  const { isChristmasMode } = useChristmasTheme();
  const { isRepublicDayMode } = useRepublicDayTheme();
  
  // Check if current page is public (not dashboard)
  const isPublicPage = !location.pathname.startsWith('/dashboard');
  
  return (
    <footer className="bg-[#1b2d4a] text-white">
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-8">
          {/* SEBI Registration Details */}
          <div>
            <h3 className="font-bold mb-4 text-lg">SEBI Registration Details:</h3>
            <div className="border-t border-yellow-400 w-16 mb-4"></div>
            <div className="text-sm space-y-2 text-gray-300">
              <p><strong>Registered Name:</strong> {contactInfo.sebiDetails.registeredName}</p>
              <p><strong>Type of registration:</strong> {contactInfo.sebiDetails.typeOfRegistration}</p>
              <p><strong>Legal Name:</strong> {contactInfo.sebiDetails.legalName}</p>
              <p><strong>SEBI Registration No.</strong> {contactInfo.sebiDetails.sebiRegNo}</p>
            </div>

            <h3 className="font-bold mb-4 mt-8 text-lg">SEBI Office Details:</h3>
            <div className="border-t border-yellow-400 w-16 mb-4"></div>
            <div className="text-sm text-gray-300">
              <p>{contactInfo.officeAddress.building}</p>
              <p>{contactInfo.officeAddress.complex}</p>
              <p>{contactInfo.officeAddress.city}</p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-bold mb-4 text-lg">Contact</h3>
            <div className="border-t border-yellow-400 w-16 mb-4"></div>
            <div className="space-y-4 mb-6">
              <div className="flex items-center gap-3">
                <div className="bg-yellow-400/20 p-2 rounded">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                  </svg>
                </div>
                <a href={`tel:${contactInfo.phone}`} className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {contactInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-yellow-400/20 p-2 rounded">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
                <a href={`mailto:${contactInfo.email}`} className="text-gray-300 hover:text-yellow-400 transition-colors">
                  {contactInfo.email}
                </a>
              </div>
            </div>

            <p className="text-sm text-gray-300 leading-relaxed mb-6">
              Our past performance does not guarantee the future performance. Investment in securities market is subject 
              to market risks. Read all the related documents carefully before investing. Notwithstanding all the efforts 
              to do best research, clients should understand that investing in equities involves a risk of loss of both 
              income and principal. Please ensure that you understand fully the risks involved in investment in equities. 
              Registration granted by SEBI and certification from NISM in no way guarantee performance of the intermediary 
              or provide any assurance of returns to investors.
            </p>
          </div>

          {/* Additional Links */}
          <div>
            <h3 className="font-bold mb-4 text-lg">Additional Links</h3>
            <div className="border-t border-yellow-400 w-16 mb-4"></div>
            <ul className="space-y-2 mb-6">
              {footerLinks.additionalLinks.map((link) => (
                <li key={link.name}>
                  {link.isExternal ? (
                    <a 
                      href={investorCharterPdf} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-gray-300 hover:text-yellow-400 transition-colors"
                    >
                      {link.name}
                    </a>
                  ) : (
                    <Link 
                      to={link.path} 
                      className="text-sm text-gray-300 hover:text-yellow-400 transition-colors"
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    >
                      {link.name}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            {/* Social Media */}
            <div className="flex gap-3 mb-6">
              <a href="https://www.instagram.com/climbcapital" target="_blank" rel="noopener noreferrer" className="border border-yellow-400 text-yellow-400 p-2 rounded-full hover:bg-yellow-400 hover:text-gray-900 transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://x.com/climbcapital9" target="_blank" rel="noopener noreferrer" className="border border-yellow-400 text-yellow-400 p-2 rounded-full hover:bg-yellow-400 hover:text-gray-900 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a href="https://t.me/Climbcapital" target="_blank" rel="noopener noreferrer" className="border border-yellow-400 text-yellow-400 p-2 rounded-full hover:bg-yellow-400 hover:text-gray-900 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                </svg>
              </a>
              <a href="https://api.whatsapp.com/send/?phone=919372273366&text=Please+share+the+details+of+your+advisory+plans" target="_blank" rel="noopener noreferrer" className="border border-yellow-400 text-yellow-400 p-2 rounded-full hover:bg-yellow-400 hover:text-gray-900 transition-colors">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0012.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 012.41 5.83c0 4.54-3.7 8.23-8.24 8.23-1.48 0-2.93-.39-4.19-1.15l-.3-.17-3.12.82.83-3.04-.2-.32a8.188 8.188 0 01-1.26-4.38c.01-4.54 3.7-8.24 8.25-8.24M8.53 7.33c-.16 0-.43.06-.66.31-.22.25-.87.85-.87 2.07 0 1.22.89 2.39 1 2.56.14.17 1.76 2.67 4.25 3.73.59.27 1.05.42 1.41.53.59.19 1.13.16 1.56.1.48-.07 1.46-.6 1.67-1.18.21-.58.21-1.07.15-1.18-.07-.1-.23-.16-.48-.27-.25-.14-1.47-.74-1.69-.82-.23-.08-.37-.12-.56.12-.16.25-.64.81-.78.97-.15.17-.29.19-.53.07-.26-.13-1.06-.39-2-1.23-.74-.66-1.23-1.47-1.38-1.72-.12-.24-.01-.39.11-.5.11-.11.27-.29.37-.44.13-.14.17-.25.25-.41.08-.17.04-.31-.02-.43-.06-.11-.56-1.35-.77-1.84-.2-.48-.4-.42-.56-.43-.14 0-.3-.01-.46-.01z"/>
                </svg>
              </a>
            </div>

            {/* App Store Links */}
            <div className="space-y-3">
              <a href="https://play.google.com/store/apps/details?id=com.climbcapital.climb_capital&hl=en_IN" target="_blank" rel="noopener noreferrer" className="block">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg" 
                  alt="Get it on Google Play" 
                  className="h-12"
                />
              </a>
              <a href="https://apps.apple.com/il/app/climb-capital-services/id6755625619" target="_blank" rel="noopener noreferrer" className="block">
                <img 
                  src="https://upload.wikimedia.org/wikipedia/commons/3/3c/Download_on_the_App_Store_Badge.svg" 
                  alt="Download on App Store" 
                  className="h-12"
                />
              </a>
            </div>
          </div>
        </div>

        {/* Christmas Festive Message - Public pages only */}
        {isChristmasMode && isPublicPage && (
          <div className="christmas-footer-message">
            <span>🎄</span>
            <span>Season's Greetings from ClimbCapital! Wishing you joy, prosperity, and successful investments!</span>
            <span>🎁</span>
          </div>
        )}
        
        {/* Republic Day Message - Public pages only */}
        {isRepublicDayMode && isPublicPage && (
          <div className="republic-day-footer-message">
            <span>🇮🇳</span>
            <span>Jai Hind! Celebrating the spirit of India's Republic Day with pride and prosperity!</span>
            <span>🎆</span>
          </div>
        )}

        {/* Bottom Bar */}
        <div className="border-t border-white/20 pt-6 mt-6">
          <div className="text-center text-sm text-gray-300">
            Copyright © 2025 <Link to="/" className="text-yellow-400 hover:underline">Climb Capital Services Private Limited</Link>. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
